import { convertFromSeconds, convertToSeconds, TimeUnit } from 'utils/date';
import { isValidNumber } from 'utils/number';
import { getItemCountText } from 'utils/string';

const NOTIFICATION_CRITERIA_POINT_MAX = 10_000;
const NOTIFICATION_CRITERIA_TIME_MIN = 60; // 1 minute;
const NOTIFICATION_CRITERIA_TIME_MAX = 7 * 24 * 60 * 60; // One week in seconds

/**
 * Shared notification criteria validator logic for both points and percent fields.
 *
 * @param value - The value to validate (string or undefined)
 * @param otherValue - The value of the other field (string or undefined)
 * @param type - 'points', 'percent', or 'time'
 * @returns Error string or undefined
 */
export function notificationCriteriaValidator(
    value: string | undefined,
    otherValue: string | undefined,
    type: 'points' | 'percent' | 'time',
    unit: TimeUnit,
    hasThresholds?: boolean,
): string | undefined {
    if (!value?.trim()) {
        if (otherValue?.trim()) {
            return `Please enter notification criteria ${type}`;
        }
        return undefined;
    }

    if (!isValidNumber(value)) {
        return `Notification criteria ${type} must be a valid number`;
    }

    const numberValue = Number(value);

    if (type === 'percent' && numberValue <= 0) {
        return `Notification criteria ${type} must be greater than 0`;
    }

    if (type === 'percent' && numberValue > 100) {
        return `Notification criteria ${type} cannot be greater than 100`;
    }

    if (type === 'points' && !Number.isInteger(numberValue)) {
        return `Notification criteria ${type} must be an integer.`;
    }

    if (type === 'points' && numberValue < 2) {
        return `Notification criteria ${type} cannot be less than 2`;
    }

    if (type === 'points' && numberValue > NOTIFICATION_CRITERIA_POINT_MAX) {
        return `Notification criteria ${type} cannot be greater than ${NOTIFICATION_CRITERIA_POINT_MAX}`;
    }

    const numberValueInSeconds = convertToSeconds(numberValue, unit);
    const singularUnit = unit.substring(0, unit.length - 1);
    if (
        type === 'time' &&
        numberValueInSeconds &&
        numberValueInSeconds < NOTIFICATION_CRITERIA_TIME_MIN
    ) {
        return `Notification criteria ${type} cannot be less than ${getItemCountText(convertFromSeconds(NOTIFICATION_CRITERIA_TIME_MIN, unit) ?? 0, singularUnit)}`;
    }

    if (
        type === 'time' &&
        numberValueInSeconds &&
        numberValueInSeconds > NOTIFICATION_CRITERIA_TIME_MAX
    ) {
        return `Notification criteria ${type} cannot be greater than ${getItemCountText(convertFromSeconds(NOTIFICATION_CRITERIA_TIME_MAX, unit) ?? 0, singularUnit)}`;
    }

    if (hasThresholds === false) {
        return `Cannot set notification criteria for indicators without thresholds.`;
    }

    return undefined;
}
